import React from "react";
import {
  Card,
  Typography,
  Checkbox,
  ButtonMenu,
  Icon,
  Tag,
} from "@ann/web-components";
import { formatDocumentDate } from "../../../../../utils/dateFormatters";
import {
  CardWrapper,
  MainContainer,
  HeaderContainer,
  TitleContainer,
  OwnerContainer,
  OwnerLeftContainer,
  CanEditContainer,
  LastOpenedContainer,
  LastOpenedLeftContainer,
  TagsContainer,
  UploadStatusTags,
  NormalTags,
  Divider,
  ContextualTagsContainer,
  ContextualTagsWrapper,
} from "./DocumentCard.styled";

const DocumentCard = ({
  title,
  owner,
  date,
  status,
  tags,
  checked,
  isCheckboxable,
  onCheckboxChange,
  onMouseEnter,
  onMouseLeave,
  showHover,
  size,
  onDuplicate,
  onShare,
  onRename,
  onAssignAgent,
  onMove,
  onDelete,
  onOpenDocumentDetail,
  isUploading = false,
  uploadProgress = { current: 0, total: 0 },
  disabledActions = { download: true, rename: false, share: false, assignAgent: false, duplicate: false, move: false, delete: false },
  hiddenActions = { download: false, rename: false, share: false, assignAgent: false, duplicate: false, move: false, delete: false },
}) => {
  const handleCardClick = () => {
  };

  const handleCardDoubleClick = () => {
    console.log('Double click detected on document card');
    // Double click opens the document detail slideout
    if (onOpenDocumentDetail) {
      console.log('Calling onOpenDocumentDetail');
      onOpenDocumentDetail();
    } else {
      console.log('onOpenDocumentDetail is not defined');
    }
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
    onCheckboxChange()
  };

  const handleButtonMenuClick = (event) => {
    event.stopPropagation();
  };

  const handlePreventDoubleClick = (event) => {
    event.stopPropagation();
  };

  const getStateColor = (state) => {
    switch (state) {
      case "Connected Agent":
        return "success";
      case "Not Connected":
        return "neutral";
      case "Expiring":
      case "Expired":
        return "error";
      case "Syncing":
        return "informative";
      default:
        return "secondary";
    }
  };

  const getStateIcon = (state) => {
    switch (state) {
      case "Connected Agent":
        return "bolt";
      case "Not Connected":
        return "link";
      case "Expiring":
      case "Expired":
        return "bolt";
      case "Syncing":
        return null;
      default:
        return "info";
    }
  };

  const getStateSpinner = (state) => state === "Syncing";

  const renderUploadStatus = () => {
    const { current = 0, total = 0 } = uploadProgress || {};
    const hasTotal = Number(total) > 0;
    const progressText = hasTotal
      ? `Processing ${current}/${total}`
      : "Processing…";

    return (
      <>
        <Tag color="success" size="small" text={progressText} spinner />
        <Tag color="secondary" size="small" text="Auto tagging in progress..." disabled />
      </>
    );
  };

  return (
    <CardWrapper 
      isUploading={isUploading}
      onDoubleClick={handleCardDoubleClick}
      style={{ cursor: 'pointer' }}
    >
      <Card
        onClick={handleCardClick}
        selected={checked}
        showHover={showHover}
        size={size}
      >
        <MainContainer
          isUploading={isUploading}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <HeaderContainer isCheckboxVisible={isCheckboxable} onDoubleClick={handlePreventDoubleClick}>
              <div className="document-checkbox">
               <Checkbox
                  checked={checked}
                  onChange={handleCheckboxClick}
                  size="medium"
                />
              </div>

              <div className="document-menu">
                <ButtonMenu
                  customIcon="dotsMenu"
                  iconPosition="right"
                  label=""
                  menuPosition="bottom"
                  onChange={(value) => {
                    console.log("Menu action:", value);
                  }}
                  onClick={handleButtonMenuClick}
                  size="medium"
                  type="tertiary"
                  width="auto"
                >
                  <ButtonMenu.Menu icon={true}>
                    {!hiddenActions.download && (
                      <ButtonMenu.Item
                        label="Download"
                        value="download"
                        icon="download"
                        disabled={disabledActions.download}
                      />
                    )}
                    {!hiddenActions.rename && (
                      <ButtonMenu.Item 
                        label="Rename" 
                        value="rename" 
                        icon="edit" 
                        onClick={onRename} 
                        disabled={disabledActions.rename}
                      />
                    )}
                    {!hiddenActions.share && (
                      <ButtonMenu.Item 
                        label="Share" 
                        value="share" 
                        icon="personAdd" 
                        onClick={onShare} 
                        disabled={disabledActions.share}
                      />
                    )}
                    {!hiddenActions.assignAgent && (
                      <ButtonMenu.Item
                        label="Assign Agents"
                        value="assign_agents"
                        icon="bolt"
                        onClick={onAssignAgent}
                        disabled={disabledActions.assignAgent}
                      />
                    )}
                    {!hiddenActions.duplicate && (
                      <ButtonMenu.Item
                        label="Duplicate"
                        value="duplicate"
                        icon="controlPointDuplicate"
                        onClick={onDuplicate}
                        disabled={disabledActions.duplicate}
                      />
                    )}
                    {!hiddenActions.move && (
                      <ButtonMenu.Item 
                        label="Move" 
                        value="move" 
                        icon="driveFileMove" 
                        onClick={onMove} 
                        disabled={disabledActions.move}
                      />
                    )}
                    {!hiddenActions.delete && (
                      <ButtonMenu.Item 
                        label="Delete" 
                        value="delete" 
                        icon="delete" 
                        onClick={onDelete} 
                        disabled={disabledActions.delete}
                      />
                    )}
                  </ButtonMenu.Menu>
                </ButtonMenu>
              </div>
          </HeaderContainer>
          <TitleContainer isUploading={isUploading}>
            <Icon name="description" size="medium" />
            <Typography category="subtitle" size="small" weight="bold">
              {title || "Document Title"}
            </Typography>
          </TitleContainer>
          <Divider />
          <OwnerContainer isUploading={isUploading} onDoubleClick={handlePreventDoubleClick}>
            <OwnerLeftContainer>
              <Icon name="person" size="small" />
              <Typography category="body" size="small" weight="regular">
                {owner || "Owner Name"}
              </Typography>
            </OwnerLeftContainer>
            {/*<CanEditContainer>
              <Icon name="edit" size="small" />
              <Typography category="body" size="small" weight="regular">
                Can View
              </Typography>
            </CanEditContainer>*/}
          </OwnerContainer>
          <LastOpenedContainer isUploading={isUploading} onDoubleClick={handlePreventDoubleClick}>
            <LastOpenedLeftContainer>
              <Icon name="lastUpdated" size="small" />
              <Typography category="body" size="small" weight="regular">
                {formatDocumentDate(date)}
              </Typography>
            </LastOpenedLeftContainer>
          </LastOpenedContainer>
          <TagsContainer isColumn={isUploading} onDoubleClick={handlePreventDoubleClick}>
            <UploadStatusTags isUploading={isUploading}>
              {renderUploadStatus()}
            </UploadStatusTags>
            <NormalTags isUploading={isUploading}>
              {status && (
                <Tag
                  color={getStateColor(status)}
                  icon={getStateIcon(status)}
                  spinner={getStateSpinner(status)}
                  size="small"
                  text={status}
                />
              )}

              {tags?.slice(0, 5).map((tag, index) => (
                <Tag
                  key={index}
                  color="secondary"
                  onClose={() => { }}
                  size="small"
                  text={tag}
                />
              ))}
              {tags && tags.length > 5 && (
                <Tag
                  color="secondary"
                  size="small"
                  text={`+${tags.length - 5}`}
                />
              )}
            </NormalTags>
          </TagsContainer>

          <Divider isUploading={isUploading} />

          <ContextualTagsContainer isUploading={isUploading} isChecked={checked} onDoubleClick={handlePreventDoubleClick}>
            <ContextualTagsWrapper className="contextual-tags-wrapper">
              <Tag
                color="informative"
                icon="group"
                onClose={() => { }}
                size="small"
                text="1 shared"
              />
              <Tag
                color="informative"
                icon="aiChat"
                onClose={() => { }}
                size="small"
                text="1 agent"
              />
            </ContextualTagsWrapper>
          </ContextualTagsContainer>
        </MainContainer>
      </Card>
    </CardWrapper>
  );
};

export default DocumentCard;
